import React, { useEffect, useState } from "react";
import axios from "axios";
import { FileText, Sparkles } from "lucide-react";
import DocumentView from "./DocumentView";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ida Creates: run any registered Skill on this project. Capabilities, not pages.
export default function IdaCreates({ project, onProject }) {
  const [skills, setSkills] = useState([]);
  const [selected, setSelected] = useState(null);
  const [instruction, setInstruction] = useState("");
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [viewing, setViewing] = useState(null);
  const outputs = project.outputs || [];

  useEffect(() => {
    axios.get(`${API}/hq/skills`).then(({ data }) => setSkills(data.skills || [])).catch(() => {});
  }, []);

  const run = async () => {
    if (!selected || running) return;
    setRunning(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API}/hq/projects/${project.id}/skills/${selected}`,
        { instruction: instruction.trim() || null },
      );
      const pendingId = data.output.id;
      setInstruction("");
      // Ida works in the background; poll until the document is ready.
      for (let i = 0; i < 110; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        const { data: fresh } = await axios.get(`${API}/hq/projects/${project.id}`);
        const out = (fresh.project.outputs || []).find((o) => o.id === pendingId);
        if (out && out.status !== "running") {
          onProject(fresh.project);
          if (out.status === "ready") setViewing(out);
          else if (out.status === "needs_input") setError(null);
          else setError("Ida could not finish this document. Try again.");
          setRunning(false);
          return;
        }
      }
      setError("This is taking longer than usual. It will appear in the list when ready.");
    } catch (e) {
      setError(e?.response?.data?.detail || "Ida could not start this document. Try again.");
    } finally {
      setRunning(false);
    }
  };

  const current = skills.find((s) => s.key === selected);

  const refreshProject = async () => {
    try {
      const { data } = await axios.get(`${API}/hq/projects/${project.id}`);
      onProject(data.project);
    } catch (e) {
      console.warn("hq project refresh failed", e);
    }
  };

  return (
    <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-5 md:p-6" data-testid="hq-ida-creates">
      <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.14em] text-white/40 mb-4">
        <Sparkles className="w-3.5 h-3.5" /> Ida Creates
      </div>

      <div className="flex flex-wrap gap-2">
        {skills.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setSelected(selected === s.key ? null : s.key)}
            className={`px-3.5 py-1.5 rounded-full text-[12.5px] border transition-colors ${
              selected === s.key
                ? "bg-white text-[#0c111c] border-white"
                : "text-white/60 border-white/15 hover:border-white/40"
            }`}
            data-testid={`hq-skill-${s.key}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {current && (
        <div className="mt-4">
          <p className="text-white/40 text-[12.5px] leading-[1.6]">{current.description}</p>
          <textarea
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            rows={2}
            placeholder="Optional: steer Ida (angle, emphasis, anything she should factor in)…"
            className="mt-3 w-full rounded-xl bg-white/[0.05] border border-white/10 px-4 py-2.5 text-[13px] text-white placeholder-white/25 focus:outline-none focus:border-white/30 resize-none"
            data-testid="hq-skill-instruction"
          />
          <button
            type="button"
            onClick={run}
            disabled={running}
            className="mt-2 w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-white text-[#0c111c] text-[13.5px] font-semibold hover:bg-white/90 transition-colors disabled:opacity-40"
            data-testid="hq-skill-run"
          >
            {running ? "Ida is thinking it through…" : `Create ${current.label}`}
          </button>
          {running && <p className="mt-2 text-white/30 text-[11.5px]">Full Ida: she thinks it through, creates, then critiques her own draft before you see it. 3 to 5 minutes.</p>}
        </div>
      )}

      {error && <p className="mt-3 text-red-300/80 text-[12.5px]">{error}</p>}

      {outputs.length > 0 && (
        <div className="mt-5 pt-4 border-t border-white/[0.07] space-y-1.5">
          {[...outputs].reverse().map((o) => (
            <div key={o.id}>
              <button
                type="button"
                onClick={() => (o.status === "ready" || !o.status ? setViewing(o) : null)}
                className="w-full flex items-center gap-2.5 text-left px-3 py-2 rounded-lg hover:bg-white/[0.05] transition-colors group"
                data-testid={`hq-output-${o.id}`}
              >
                <FileText className={`w-3.5 h-3.5 shrink-0 ${o.status === "running" ? "text-amber-300/60 animate-pulse" : o.status === "needs_input" ? "text-sky-300/70" : "text-white/30 group-hover:text-white/60"}`} />
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="text-white/80 text-[13px] truncate">{o.title}</span>
                    {o.visibility === "client" && (
                      <span className="shrink-0 rounded-full px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] border bg-sky-300/10 text-sky-200 border-sky-300/25" data-testid={`hq-output-shared-${o.id}`}>
                        Shared
                      </span>
                    )}
                  </span>
                  <span className="block text-white/30 text-[11px]">
                    {o.label}
                    {o.status === "running" ? " · Ida is writing…"
                      : o.status === "needs_input" ? " · Ida has questions first"
                      : o.status === "failed" ? " · failed"
                      : ` · ${new Date(o.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}`}
                    {typeof o.confidence === "number" && o.status === "ready" ? ` · ${o.confidence}%` : ""}
                  </span>
                </span>
              </button>
              {o.status === "needs_input" && Array.isArray(o.questions) && (
                <div className="ml-9 mb-2 rounded-lg bg-sky-300/[0.07] border border-sky-300/20 p-3" data-testid={`hq-output-questions-${o.id}`}>
                  <p className="text-sky-200/80 text-[11.5px] mb-1.5">Before guessing, Ida would ask:</p>
                  {o.questions.map((q, i) => (
                    <p key={i} className="text-white/70 text-[12.5px] leading-[1.6]">· {q}</p>
                  ))}
                  <button
                    type="button"
                    onClick={() => { setSelected(o.skill); setInstruction("Answers to your questions: "); }}
                    className="mt-2 text-[12px] text-sky-300 hover:text-sky-200 underline underline-offset-2"
                    data-testid={`hq-output-answer-${o.id}`}
                  >
                    Answer and rerun
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {viewing && <DocumentView output={viewing} projectId={project.id} onClose={() => setViewing(null)} onChanged={refreshProject} />}
    </div>
  );
}
